"use client";
import React, { useEffect, useState, createContext, useContext } from "react";
import axios from "./axiosConfig";
import { setCookie, getCookie, deleteCookie } from "cookies-next";
import { COOKIE_PATH } from "./utils/basePath";

type User = {
  id: number;
  nome: string;
  senha: string;
  token: string;
  permissions: string[];
  login?: string;
  grupoUsuarioId?: number;
};

type AuthContextType = {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  userCan: (permissions: string[]) => boolean;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType | null>(null);

const cookieOptions = { path: COOKIE_PATH };

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserFromToken = async () => {
      try {
        const savedUser = JSON.parse(localStorage.getItem("user") || "null");
        const cookieToken = await getCookie("token");
        const savedToken =
          (typeof cookieToken === "string" && cookieToken) ||
          localStorage.getItem("token") ||
          "";
        const savedPermissions = JSON.parse(localStorage.getItem("permissions") || "[]");

        if (savedUser && savedToken) {
          setUser({
            id: savedUser.id,
            nome: savedUser.nome,
            senha: "",
            token: String(savedToken),
            permissions: savedPermissions,
            login: savedUser.login ?? savedUser.nome,
            grupoUsuarioId: savedUser.grupoUsuarioId ?? savedUser.grupousuario_id,
          });
          // Garante cookie legível após reload em hosting estático / PWA
          setCookie("token", String(savedToken), cookieOptions);
        }
      } catch (error) {
        console.error("Erro ao restaurar sessão:", error);
      } finally {
        setLoading(false);
      }
    };

    loadUserFromToken();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const { data } = await axios.post("/api/Usuarios/authenticate", {
        Nome: username,
        Senha: password,
        token: "",
        permissions: [],
        id: "0",
      });

      const nextUser: User = {
        id: data.id,
        nome: data.nome,
        senha: "",
        token: data.token,
        permissions: data.permissions,
        login: data.nome,
        grupoUsuarioId: data.grupoUsuarioId || data.grupousuario_id,
      };

      setCookie("token", data.token, cookieOptions);
      localStorage.setItem("user", JSON.stringify(nextUser));
      localStorage.setItem("token", data.token);
      localStorage.setItem("permissions", JSON.stringify(data.permissions));
      setUser(nextUser);
    } catch (error) {
      console.error("Erro no login", error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("permissions");
    deleteCookie("token", cookieOptions);
    setUser(null);
  };

  const userCan = (permissions: string[]) => {
    if (!user || !user.permissions) return false;
    const userPermissionsLower = user.permissions.map((perm) => perm.toLowerCase());
    const requiredPermissionsLower = permissions.map((perm) => perm.toLowerCase());
    return requiredPermissionsLower.some((permission) =>
      userPermissionsLower.includes(permission)
    );
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, userCan, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
